'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();

import { usePathname } from 'next/navigation'
import { useCallback, useEffect } from 'react'

import { useCalendly } from '@/components/integrations/calendly/calendly-provider'
import { useIntercom } from '@/components/integrations/intercom/intercom-provider'

type LinkActionListenerProps = {
  cleanupQuery?: boolean
}

type LinkAction = 'request-demo' | 'chat'

/** What a link is asking to open, if anything. `demo=1` is the older spelling of `request-demo`. */
function requestedAction(params: URLSearchParams): LinkAction | null {
  const action = params.get('action')
  if (action === 'chat') {
    return 'chat'
  }
  return action === 'request-demo' || params.get('demo') === '1' ? 'request-demo' : null
}

/**
 * Opens the demo planner or the chat when a link asks for it.
 *
 * Content cannot call `useCalendly` or `useIntercom` — an article is MDX, not a client component —
 * so it asks in the URL instead: `?action=request-demo` or `?action=chat`. Two things then have to
 * happen. A visitor arriving on such a URL (a campaign link, a shared one, a new tab) gets the
 * dialog on load, and the parameter is dropped so a reload does not reopen it. A visitor clicking
 * such a link inside an article gets the dialog then and there — travelling to another page to be
 * shown a popup loses both the article and the point of the link.
 *
 * `?demo=1` keeps working alongside `?action=request-demo`, because links out in the world already
 * carry it, optionally with a `user` to prefill the planner from.
 */
export function LinkActionListener({ cleanupQuery = true }: LinkActionListenerProps) {
  const { open } = useCalendly()
  const { open: openChat } = useIntercom()
  const pathname = usePathname()

  const perform = useCallback(
    async (action: LinkAction, params: URLSearchParams) => {
      if (action === 'chat') {
        openChat()
        return
      }

      // Campaign links name the person they were sent to, so the planner can fill itself in.
      const user = params.get('id') ?? params.get('user')
      const prefill = user
        ? await fetch(`/api/request-demo?user=${encodeURIComponent(user)}`)
            .then((response) => (response.ok ? response.json() : null))
            .catch(() => null)
        : null

      open({ user, prefill: (prefill as { name?: string; email?: string } | null) ?? undefined })
    },
    [open, openChat],
  )

  // Arriving on a URL that asks for one.
  useEffect(() => {
    if (!pathname || typeof window === 'undefined') {
      return
    }

    const params = new URLSearchParams(window.location.search)
    const action = requestedAction(params)
    if (!action) {
      return
    }

    if (cleanupQuery) {
      // `history` rather than `router.replace`: this is only meant to tidy the address bar, and a
      // router navigation on the first render is dropped before the page has hydrated.
      window.history.replaceState(null, '', pathname)
    }
    void perform(action, params)
  }, [cleanupQuery, pathname, perform])

  // Clicking one, anywhere on the page.
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      // A modified click means "open this elsewhere"; let it navigate, and the effect above will
      // open the dialog on the page it lands on.
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) {
        return
      }

      const anchor = (event.target as Element | null)?.closest?.('a')
      if (!anchor?.href) {
        return
      }

      const url = new URL(anchor.href, window.location.href)
      if (url.origin !== window.location.origin) {
        return
      }

      const action = requestedAction(url.searchParams)
      if (!action) {
        return
      }

      event.preventDefault()
      event.stopPropagation()
      void perform(action, url.searchParams)
    }

    // Capture, because React listens at the app root: by the time a click bubbles up to the
    // document, `next/link` has already started navigating and preventing it comes too late.
    document.addEventListener('click', onClick, true)
    return () => document.removeEventListener('click', onClick, true)
  }, [perform])

  return null
}
